// Admin Home screens, and the admin SOP Library it opens. Pure: data in, HTML out.
// Home rev 3: MOCKUP_HOME_REV3.html (approved 2026-10-23, 9:00 PM). Earlier: ADMIN_HOME_WORDS.md.

import { SITE_ZONES, zoneFromState } from "./site-time";
import { COMING_LINE, DEMO_LABELS, demoLabelId, guestUrl, initialsOf, type DemoLabelKey, type HomeSiteRow, type Tile, type TileGroup } from "./admin-home";
import { stageHtml } from "./site-hub-screens";
import { REVIEW_LINE, type SopEntry, type SopLibrary } from "./resources";
import { esc, ident } from "./training-screens";

// ---- Home rev 3 (MOCKUP_HOME_REV3.html, approved 2026-10-23, 9:00 PM) ----

/** One Program Setup or Field Pages row. Quiz 1, 2 and 3 share one row. */
function setupRow(t: Tile): string {
  const amb = t.alert > 0 ? `<span class="hub-amb" aria-label="${t.alert} need you">${t.alert}</span>` : "";
  const ext = t.newTab ? '<span class="hm-ext2">New Tab</span>' : "";
  const inner = `<span class="tt"><span class="code">${esc(t.code)}</span><span><b>${esc(t.name)}</b><small>${esc(t.line)}</small></span></span>${amb}${ext}`;
  if (t.opensSheet) return `<button class="hm-row" type="button" data-act="home-guest">${inner}</button>`;
  const target = t.newTab ? ' target="_blank" rel="noopener"' : "";
  return `<a class="hm-row" href="${esc(t.href)}"${target}>${inner}</a>`;
}

function quizRow(qs: Tile[]): string {
  const links = qs
    .map((q) => `<a class="hm-qz" href="${esc(q.href)}" target="_blank" rel="noopener" aria-label="Open ${esc(q.name)}">${esc(q.code.slice(1))}</a>`)
    .join("");
  return `<div class="hm-row"><span class="tt"><span class="code">QZ</span><span><b>Quiz 1, 2, 3</b><small>Node links</small></span></span><span class="hm-qzs">${links}</span><span class="hm-ext2">New Tab</span></div>`;
}

function groupBox(g: TileGroup): string {
  const quizzes = g.tiles.filter((t) => /^Q\d$/.test(t.code));
  const rows: string[] = [];
  for (const t of g.tiles) {
    if (/^Q\d$/.test(t.code)) {
      if (t === quizzes[0]) rows.push(quizRow(quizzes));
      continue;
    }
    rows.push(setupRow(t));
  }
  const sub = g.title === "Program Setup" ? ' <small>all sites</small>' : "";
  return `<h2 class="hub-sec">${esc(g.title)}${sub}</h2><div class="box">${rows.join("")}</div>`;
}

/** HOME_SITE_ROW_WORDS.md rev 2 (approved 2026-10-26): Active or Off with the site, one clean bar on the right. */
function siteRow(r: HomeSiteRow): string {
  return `<a class="hm-site" href="#/sites/${esc(r.siteNumber)}" data-q="${esc(r.search)}">
    <span class="t"><span class="pill ${r.isActive ? "green" : "grey"}">${r.isActive ? "ACTIVE" : "OFF"}</span>
      <span class="nm"><span class="num">${esc(r.siteNumber)}</span><b>${esc(r.name)}</b></span><small>${esc(r.line)}</small></span>
    ${stageHtml(r.stage)}</a>`;
}

export interface HomeView {
  holderName: string;
  sub: string;
  cont: { href: string; siteName: string; name: string; when: string } | null;
  sitesHead: string;
  /** Null while the sites load. */
  sites: HomeSiteRow[] | null;
  sitesError: string;
  search: string;
  groups: TileGroup[];
  flash: string;
  /** Add Site: "on" for an admin covering all sites, "offline" with no signal, "" to hide it. */
  addSite?: "on" | "offline" | "";
  /** Refresh (COHESION_CHANGE_LOG.md change 1): "on", "busy" while it runs, "offline" with no signal, "" to hide it. */
  refresh?: "on" | "busy" | "offline" | "";
  /** "Updated Today 5:34 AM": the oldest saved stage. Blank when no site has one yet. */
  updated?: string;
}

function refreshButton(state: HomeView["refresh"]): string {
  if (state === "busy") return '<button class="hm-refresh" type="button" disabled>Refreshing…</button>';
  if (state === "offline") return '<button class="hm-refresh" type="button" disabled title="Needs signal">Refresh</button>';
  if (state === "on") return '<button class="hm-refresh" type="button" data-act="home-refresh">Refresh</button>';
  return "";
}

export function adminHome(v: HomeView): string {
  const cont = v.cont
    ? `<a class="hub-cont" href="${esc(v.cont.href)}"><span>Continue: ${esc(v.cont.siteName)} · ${esc(v.cont.name)}</span><small>${esc(v.cont.when)}</small></a>`
    : "";
  const list = v.sitesError
    ? `<div class="note red">${esc(v.sitesError)} <button class="chg" type="button" data-act="sites-retry">Try Again</button></div>`
    : v.sites === null
      ? '<div class="loading">Loading sites…</div>'
      : v.sites.length
        ? v.sites.map(siteRow).join("")
        : '<div class="empty">No sites yet.</div>';
  const search = v.sites && v.sites.length > 1
    ? `<input class="hm-search" id="home-site-search" type="search" value="${esc(v.search)}" placeholder="Search by name or number" aria-label="Search sites" autocomplete="off" autocapitalize="off" spellcheck="false">`
    : "";
  return `${ident(initialsOf(v.holderName), v.holderName, v.sub)}
    ${v.flash}
    ${cont}
    <div class="hub-cols">
      <div class="hub-col">
        <div class="hm-sechd"><h2 class="hub-sec">Sites${v.sitesHead ? ` <small>${esc(v.sitesHead)}</small>` : ""}${v.updated ? `<span class="hm-upd" id="home-updated">${esc(v.updated)}</span>` : ""}</h2><span class="hm-acts">${refreshButton(v.refresh ?? "")}${
          v.addSite === "on"
            ? '<button class="hm-add" type="button" data-act="add-site">Add Site</button>'
            : v.addSite === "offline"
              ? '<button class="hm-add" type="button" disabled title="Needs signal">Add Site</button>'
              : ""
        }</span></div>
        <div class="box">
          <div class="hm-pull${v.refresh === "busy" ? " on" : ""}" id="home-pull" aria-live="polite">${v.refresh === "busy" ? "Refreshing every site…" : "Let go to refresh"}</div>
          ${search}
          <div id="home-sites">${list}</div>
          <div class="empty" id="home-sites-none" hidden>No site matches.</div>
        </div>
      </div>
      <div class="hub-col">
        ${v.groups.map(groupBox).join("")}
        <div class="box hm-later"><div class="hm-row"><span class="tt"><span class="code">AS</span><span><b>All Sites Summary</b><small>Every site side by side, with filters. Built later.</small></span></span></div></div>
        <div class="muted hm-coming">${esc(COMING_LINE)}</div>
      </div>
    </div>`;
}

/** Shows the Home site rows that match the search box. Returns how many show. */
export function filterHomeSites(host: ParentNode, query: string): number {
  const q = query.trim().toLowerCase();
  let shown = 0;
  host.querySelectorAll<HTMLElement>(".hm-site").forEach((el) => {
    const hit = !q || (el.dataset.q ?? "").includes(q);
    el.hidden = !hit;
    if (hit) shown += 1;
  });
  return shown;
}

// ---- Guest Page sheet (screen 3) ----

export function guestSheet(sites: { siteNumber: string; name: string }[], site: string, key: DemoLabelKey): string {
  const siteBtns = sites
    .map((s) => `<label class="segk"><input type="radio" name="gp-site" value="${esc(s.siteNumber)}"${s.siteNumber === site ? " checked" : ""}><span title="${esc(s.name)}">${esc(s.siteNumber)}</span></label>`)
    .join("");
  const labelBtns = DEMO_LABELS.map(
    (d) => `<label class="segk wide"><input type="radio" name="gp-label" value="${d.key}"${d.key === key ? " checked" : ""}><span>${esc(d.label)}</span></label>`,
  ).join("");
  return `<div class="shade"><div class="sheet" role="dialog" aria-modal="true" aria-label="Open the Guest Page">
    <div class="big">Open the Guest Page</div>
    <p>Opens a demo label as anyone scanning it would see it. It opens in a new tab.</p>
    <span class="lbl hm-lbl">Site</span><div class="seg2">${siteBtns}</div>
    <span class="lbl hm-lbl">Demo label</span><div class="seg2">${labelBtns}</div>
    <p class="mono hm-id" id="gp-id">${esc(demoLabelId(site, key))}</p>
    <a class="go hm-go" id="gp-open" href="${esc(guestUrl(site, key))}" target="_blank" rel="noopener" data-act="home-guest-open">Open Guest Page</a>
    <button class="go2" type="button" data-act="sheet-cancel">Cancel</button>
  </div></div>`;
}

// ---- SOP Library, admin review (Field Pages tile) ----

function sopRow(s: SopEntry): string {
  const tasks = s.tasks.length ? s.tasks.join(", ") : "Covers every Tier 3 job";
  return `<a class="menu-row" href="#/sop-library/${encodeURIComponent(s.sopNumber)}"><span class="num">${esc(s.chip)}</span><span class="t"><b>${esc(s.title)}</b><small>${esc(tasks)} · Rev ${esc(s.version)}</small></span><span class="chev">›</span></a>`;
}

export function sopLibraryScreen(lib: SopLibrary): string {
  const permit = lib.permit ? `<h2 class="sec">Permit</h2><div class="box">${sopRow(lib.permit)}</div>` : "";
  const tiers = lib.tiers
    .filter((t) => t.sops.length)
    .map((t) => `<h2 class="sec">${esc(t.title)} <small class="muted">${esc(t.sub)}</small></h2><div class="box">${t.sops.map(sopRow).join("")}</div>`)
    .join("");
  return `${ident("SL", "SOP Library", `${lib.total} SOPs · the list a QEW sees in Resources`)}
    <div class="note">${esc(REVIEW_LINE)} Only SOPs of switched-on tasks show, same as Resources.</div>
    ${permit}${tiers || '<div class="empty">No SOPs on switched-on tasks yet.</div>'}`;
}

export function sopReviewScreen(s: SopEntry, frameUrl: string | null, error = ""): string {
  const body = error
    ? `<div class="note red">${esc(error)}</div><button class="prim" type="button" data-act="sop-review-retry">Try Again</button>`
    : frameUrl
      ? `<div class="res-review">${esc(REVIEW_LINE)}</div><iframe class="res-frame tk-frame" title="${esc(s.sopNumber)}" src="${esc(frameUrl)}"></iframe>`
      : `<div class="loading">Opening ${esc(s.sopNumber)}…</div>`;
  return `<a class="back" href="#/sop-library">SOP Library</a>${ident("SOP", s.title, `${s.sopNumber} · review only, as the QEW sees it in Resources`)}${body}`;
}

// ---- Add Site sheet (MOCKUP_HOME_REV3.html, ADD_SITE_WORDS.md, approved 2026-10-23) ----

export interface AddSiteEntry {
  siteNumber: string;
  name: string;
  city: string;
  state: string;
  /** The number came from a plant chip, not the next number up (COHESION_CHANGE_LOG.md change 4). */
  fromPlant?: boolean;
  /** The plant's time zone (SITE_TIME_ZONE_WORDS.md). Filled from a one-zone state; blank until picked for a two-zone state. */
  timeZone?: string;
  /** Robert picked the zone himself: typing the state no longer changes it. */
  zonePicked?: boolean;
}

/** The Time Zone line under State on Add Site: the hint and the amber note, kept in step as the state is typed. */
export function addSiteZoneHint(state: string, zone: string): { from: string; note: string } {
  const st = state.trim().toUpperCase();
  const fromState = zoneFromState(st);
  return {
    from: zone && fromState === zone ? `from ${st}` : "",
    note: !zone && /^[A-Z]{2}$/.test(st) ? `${st} has two zones. Pick the one this plant runs on.` : "",
  };
}

/**
 * step: the line shown while it works ("Making the site and its tags…").
 * numberNote: why the number is what it is. The number is shown, never typed (change 4).
 */
export function addSiteSheet(
  plants: readonly { siteNumber: string; name: string; city: string; state: string }[],
  v: AddSiteEntry,
  busy: boolean,
  error: string,
  step: string,
  numberNote = "",
): string {
  const n = v.siteNumber.trim() || "NN";
  const nm = v.name.trim() || "Name";
  const hint = addSiteZoneHint(v.state, v.timeZone ?? "");
  const chips = plants.length
    ? `<div class="as-plants"><span>From Plants:</span>${plants
        .map((p) => {
          const on = v.fromPlant && v.siteNumber === p.siteNumber;
          return `<button type="button"${on ? ' class="on" aria-pressed="true"' : ""} data-act="add-site-plant" data-plant="${esc(p.siteNumber)}"${busy ? " disabled" : ""}>${esc(p.name)}${p.state ? `, ${esc(p.state)}` : ""}</button>`;
        })
        .join("")}</div>`
    : "";
  const field = (key: "name" | "city" | "state", label: string, extra = "") =>
    `<label class="as-fl"><span>${label}</span><input class="fin${key === "state" ? " as-st" : ""}" name="${key}" value="${esc(v[key])}"${extra}${busy ? " disabled" : ""} autocomplete="off"></label>`;
  const made = [
    ["The site record", "Number locked. It starts every Asset ID here. Off until you turn it on."],
    ["Drive site folder", `${n} ${nm}, with 00 Field Labels through 09 Customer Documents`],
    ["Field Asset ID Creator workbook", "A copy of the standard workbook in 01 Asset IDs"],
    ["QEW tags", `${n}-QEW01 to ${n}-QEW99, ready to print in 03 QEW Tags`],
    ["Its hub", "Program 00 to 08, all Not Started. Add the Plant Manager from Site Contacts."],
  ]
    .map(([b, sm], i) => `<div><span class="tick">✓</span><span><b>${esc(b!)}</b><small${i === 1 ? ' id="as-folder"' : i === 3 ? ' id="as-tags"' : ""}>${esc(sm!)}</small></span></div>`)
    .join("");
  return `<div class="shade"><div class="sheet" role="dialog" aria-modal="true" aria-label="Add Site">
    <div class="big">Add Site</div>
    <p>Builds the site and everything it needs, the same at every site. It starts Off until you turn it on.</p>
    ${chips}
    <form id="add-site-form" novalidate>
      <div class="as-fl"><span>Site No.</span><span class="as-numrow"><span class="as-numv mono" id="as-num">${esc(v.siteNumber || "None")}</span><small>${esc(numberNote)}</small></span></div>
      ${field("name", "Name")}
      ${field("city", "City")}
      ${field("state", "State", ' maxlength="2" autocapitalize="characters"')}
      <label class="as-fl"><span>Time Zone</span><span class="as-tzrow"><select class="fin as-tz${v.timeZone ? "" : " amber"}" name="timeZone" id="as-tz"${busy ? " disabled" : ""}>
        <option value=""${v.timeZone ? "" : " selected"}></option>${SITE_ZONES.map((z) => `<option value="${esc(z.id)}"${z.id === v.timeZone ? " selected" : ""}>${esc(z.name)}</option>`).join("")}
      </select><small id="as-tz-from">${esc(hint.from)}</small></span></label>
      <div class="note" id="as-tz-note"${hint.note ? "" : " hidden"}>${esc(hint.note)}</div>
    </form>
    <div class="as-make"><div>What gets made</div>${made}</div>
    ${error ? `<div class="note red">${esc(error)}</div>` : ""}
    ${busy && step ? `<div class="note">${esc(step)}</div>` : ""}
    <button class="go" type="button" data-act="add-site-confirm" id="as-go"${busy || !v.timeZone ? " disabled" : ""}>${busy ? "Adding…" : "Add Site"}</button>
    <button class="go2" type="button" data-act="sheet-cancel"${busy ? " disabled" : ""}>Cancel</button>
  </div></div>`;
}

/** Keeps the folder and tag lines in step with what is typed, without drawing the sheet again. */
export function addSiteLines(siteNumber: string, name: string): { folder: string; tags: string } {
  const n = siteNumber.trim() || "NN";
  const nm = name.trim() || "Name";
  return { folder: `${n} ${nm}, with 00 Field Labels through 09 Customer Documents`, tags: `${n}-QEW01 to ${n}-QEW99, ready to print in 03 QEW Tags` };
}

/** Why the Add Site number is what it is. */
export function addSiteNumberNote(v: AddSiteEntry, plantName: string): string {
  if (!v.siteNumber) return "Every number 00 to 99 is used.";
  if (v.fromPlant) return `${plantName || "The plant"}'s number from your Plants list.`;
  const n = Number(v.siteNumber);
  return n > 0 ? `Next number up. The last one used is ${String(n - 1).padStart(2, "0")}.` : "Next number up.";
}
